<template>
  <v-dialog
    v-model="showModal"
    persistent
    fullscreen
  >
    <div class="content-section">
      <overlay-alert
        :show-progress="showProgress"
        :process-alert="processAlert"
      />
      <h1 class="header-style">
        {{ activeCheckInDetails.type | arrangePretty }} CheckOut
      </h1>
      <p class="subheader-style">
        Fill the fields to checkout.
      </p>
      <div
        v-if="verificationFailed"
        class="fail-message-style mb-4"
      >
        <p class="fail-style">
          <v-icon
            color="#C62828"
            size="30"
          >
            mdi-alert
          </v-icon> <br>
          {{ verificationFailedMessage }}
        </p>
      </div>
      <v-form
        ref="form"
        v-model="valid"
        class="mt-6"
        lazy-validation
      >
        <v-col
          class="checkout-type"
          cols="12"
        >
          <p
            v-if="locationCheck"
            class="p-0 m-0"
          >
            Checkout Type
          </p>

          <v-radio-group
            v-if="locationCheck"
            v-model="checkoutType"
            row
            class="p-0 m-0"
            light
            :rules="[(v) => !!v || 'This field is required']"
            @change="isCheckoutTypeChanged"
          >
            <v-radio
              label="Onsite"
              value="onsite"
              light
              color="#37474F"
            >
              <template #label>
                <p class="type-bottom">
                  Onsite
                </p>
              </template>
            </v-radio>
            <v-radio
              label="Offsite"
              value="offsite"
              light
              color="#37474F"
            >
              <template #label>
                <p class="type-bottom">
                  Offsite
                </p>
              </template>
            </v-radio>
          </v-radio-group>

          <div v-if="isOnsiteSelected">
            <v-col
              v-if="gatesList.length > 1"
              cols="12"
              class="pt-4 checkout-type"
            >
              <p class="p-0 m-0">
                Select Gate
              </p>
              <v-select
                v-model="selectedGate"
                light
                color="black"
                flat
                dense
                :disabled="previousGate !== undefined && previousGate !== null"
                :items="gatesList"
                :rules="[(v) => !!v || 'This field is required']"
                item-text="name"
                item-value="id"
                label="Gates"
                outlined
                background-color="#FFF"
                class="small-select checkin"
              />
            </v-col>
            <v-row v-if="selectedGate">
              <v-col
                cols="12"
                class="pb-0"
              >
                <div class="location-wrapper">
                  <div class="location-label-row">
                    <v-icon
                      size="16"
                      class="location-icon"
                    >
                      mdi-domain
                    </v-icon>
                    <span class="location-label"> Project </span>
                  </div>

                  <div class="location-value">
                    {{ selectedProjectName }}
                  </div>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="pt-0"
              >
                <div class="location-wrapper">
                  <div class="location-label-row">
                    <v-icon
                      size="16"
                      class="location-icon"
                    >
                      mdi-map-marker
                    </v-icon>
                    <span class="location-label"> Location </span>
                  </div>

                  <div class="location-value">
                    {{ selectedGateLocation }}
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-col
              v-if="activeCheckInDetails.type === 'visitor'"
              class="checkout-type"
            >
              <v-radio-group
                v-model="visitStatus"
                row
                :rules="[(v) => !!v || 'This field is required']"
                class="field-text-style"
                light
                @change="isVisitStatusChanged"
              >
                <v-radio
                  label="Pending"
                  value="pending"
                  light
                  color="#37474F"
                >
                  <template #label>
                    <span class="input__label">Pending</span>
                  </template>
                </v-radio>
                <v-radio
                  label="Completed"
                  value="completed"
                  light
                  color="#37474F"
                >
                  <template #label>
                    <span class="input__label">Completed</span>
                  </template>
                </v-radio>
              </v-radio-group>
              <v-textarea
                v-if="visitStatus !== 'completed'"
                ref="status"
                v-model="pendingReason"
                dense
                :rules="
                  visitStatus === 'pending'
                    ? [(v) => !!v || 'This field is required']
                    : []
                "
                persistent-hint
                color="#37474F"
                class="field-text-style"
                background-color="#FFF"
                light
                :disabled="visitStatus !== 'pending'"
                outlined
                label="Visit Status"
              />
            </v-col>
            <v-col
              v-if="activeCheckInDetails.type === 'staff'"
              class="checkout-type"
            >
              <v-progress-linear
                v-model="currentProjectProgress"
                height="40"
                color="#64CA7F"
                light
                rounded
              >
                <strong>{{ Math.ceil(currentProjectProgress) }}%</strong>
              </v-progress-linear>
              <br>
              <v-text-field
                v-model="updatedProjectProgress"
                dense
                hint="Project progress value."
                persistent-hint
                class="field-text-style"
                inputmode="numeric"
                color="#000000"
                light
                outlined
                background-color="#FFF"
                label="Update Project Progress*"
              />
            </v-col>
            <v-col
              v-if="activeCheckInDetails.type === 'contractor'"
              class="checkout-type"
            >
              <div class="progress-linear-style">
                <v-progress-linear
                  v-model="currentContractProgress"
                  height="40"
                  color="#64CA7F"
                  light
                  rounded
                >
                  <strong>{{ Math.ceil(currentContractProgress) }}%</strong>
                </v-progress-linear>
              </div>
              <br>
              <v-text-field
                v-model="updatedContractProgress"
                dense
                hint="Contract progress value."
                persistent-hint
                class="field-text-style"
                inputmode="numeric"
                color="#37474F"
                light
                outlined
                background-color="#FFF"
                label="Update Contract Progress*"
              />
            </v-col>

            <v-col
              v-if="isWorkTypeIssue"
              class="checkout-type"
              cols="12"
            >
              <p>Issue Status</p>
              <br>

              <div class="checkout-details">
                <v-radio-group
                  v-model="issueStatus"
                  row
                  class="field-text-style"
                  light
                  :rules="[(v) => !!v || 'This field is required']"
                  @change="isIssueStatusChanged"
                >
                  <v-radio
                    label="Pending"
                    value="pending"
                    light
                    color="#FF3700"
                  >
                    <template #label>
                      <span class="input__label">Pending</span>
                    </template>
                  </v-radio>
                  <v-radio
                    label="Completed"
                    value="completed"
                    light
                    color="#FF3700"
                  >
                    <template #label>
                      <span class="input__label">Completed</span>
                    </template>
                  </v-radio>
                </v-radio-group>
                <v-textarea
                  v-if="showReasonField"
                  ref="status"
                  v-model="pendingReason"
                  dense
                  :rules="
                    issueStatus === 'pending'
                      ? [(v) => !!v || 'This field is required']
                      : []
                  "
                  class="field-text-style"
                  hint="Only required if Issue Status is 'Pending'."
                  persistent-hint
                  color="#000000"
                  light
                  outlined
                  background-color="#FFF"
                  label="Reason*"
                />
                <v-textarea
                  v-if="showRootCauseField"
                  ref="status"
                  v-model="rootCause"
                  dense
                  :rules="
                    issueStatus === 'completed'
                      ? [(v) => !!v || 'This field is required']
                      : []
                  "
                  class="field-text-style"
                  hint="Only required if Issue Status is 'Completed'."
                  persistent-hint
                  color="#000000"
                  light
                  outlined
                  background-color="#FFF"
                  label="Root Cause of Issue*"
                />
              </div>
            </v-col>
          </div>
          <div v-if="isOffsiteSelected">
            <v-textarea
              v-model="manualCheckoutReason"
              outlined
              background-color="#FFF"
              light
              class="field-text-style"
              :counter="100"
              counter-value-color="red"
              :rules="[
                (v) =>
                  (v && v.length < 100) ||
                  'Reason must be 100 characters or less',
              ]"
              hint="Reason for requesting a manual check-out."
              persistent-hint
              color="#000000"
              label="Reason*"
              dense
            />
            <v-datetime-picker
              v-model="checkOutTime"
              date-format="yyyy-MM-dd"
              label="Actual Check-out Time*"
              time-format="HH:mm:ss"
              :dialog-width="300"
              :text-field-props="{
                light: light,
                backgroundColor: '#FFF',
                outlined: outlined,
                dense: dense,
                hint: 'Please specify the time you actually checked out of the site.',
                persistentHint: true,
                suffix: userTimeZone,
                class: 'field-text-style',
                rules: [(v) => !!v || 'Check-out Time is required'],
              }"
              :date-picker-props="{
                headerColor: 'black',
                light: true,
                min: minDate,
              }"
              :time-picker-props="{
                headerColor: 'black',
                light: true,
                min: minTime,
                max: maxTime,
              }"
            >
              <template #dateIcon>
                <v-icon
                  large
                  color="white"
                >
                  mdi-calendar
                </v-icon>
              </template>
              <template #timeIcon>
                <v-icon
                  color="white"
                  large
                >
                  mdi-alarm
                </v-icon>
              </template>
            </v-datetime-picker>
            <v-col
              v-if="activeCheckInDetails.type === 'visitor'"
              class="checkout-type"
            >
              <v-radio-group
                v-model="visitStatus"
                row
                :rules="[(v) => !!v || 'This field is required']"
                class="field-text-style"
                light
                @change="isVisitStatusChanged"
              >
                <v-radio
                  label="Pending"
                  value="pending"
                  light
                  color="#37474F"
                >
                  <template #label>
                    <span class="input__label">Pending</span>
                  </template>
                </v-radio>
                <v-radio
                  label="Completed"
                  value="completed"
                  light
                  color="#37474F"
                >
                  <template #label>
                    <span class="input__label">Completed</span>
                  </template>
                </v-radio>
              </v-radio-group>
              <v-textarea
                ref="status"
                v-model="pendingReason"
                dense
                :rules="
                  visitStatus === 'pending'
                    ? [(v) => !!v || 'This field is required']
                    : []
                "
                persistent-hint
                color="#37474F"
                class="field-text-style"
                background-color="#FFF"
                light
                :disabled="visitStatus !== 'pending'"
                outlined
                label="Visit Status"
              />
            </v-col>
            <v-col
              v-if="activeCheckInDetails.type === 'staff'"
              class="checkout-type"
            >
              <v-progress-linear
                v-model="currentProjectProgress"
                height="40"
                color="#64CA7F"
                light
                rounded
              >
                <strong>{{ Math.ceil(currentProjectProgress) }}%</strong>
              </v-progress-linear>
              <br>
              <v-text-field
                v-model="updatedProjectProgress"
                dense
                hint="Project progress value."
                persistent-hint
                class="field-text-style"
                inputmode="numeric"
                color="#000000"
                light
                outlined
                background-color="#FFF"
                label="Update Project Progress*"
              />
            </v-col>
            <v-col
              v-if="activeCheckInDetails.type === 'contractor'"
              class="checkout-type"
            >
              <div class="progress-linear-style">
                <v-progress-linear
                  v-model="currentContractProgress"
                  height="40"
                  color="#64CA7F"
                  light
                  rounded
                >
                  <strong>{{ Math.ceil(currentContractProgress) }}%</strong>
                </v-progress-linear>
              </div>
              <br>
              <v-text-field
                v-model="updatedContractProgress"
                dense
                hint="Contract progress value."
                persistent-hint
                class="field-text-style"
                inputmode="numeric"
                color="#37474F"
                light
                outlined
                background-color="#FFF"
                label="Update Contract Progress*"
              />
            </v-col>

            <v-col
              v-if="isWorkTypeIssue"
              class="checkout-type"
              cols="12"
            >
              <p>Issue Status</p>
              <br>

              <div class="checkout-details">
                <v-radio-group
                  v-model="issueStatus"
                  row
                  class="field-text-style"
                  light
                  :rules="[(v) => !!v || 'This field is required']"
                  @change="isIssueStatusChanged"
                >
                  <v-radio
                    label="Pending"
                    value="pending"
                    light
                    color="#FF3700"
                  >
                    <template #label>
                      <span class="input__label">Pending</span>
                    </template>
                  </v-radio>
                  <v-radio
                    label="Completed"
                    value="completed"
                    light
                    color="#FF3700"
                  >
                    <template #label>
                      <span class="input__label">Completed</span>
                    </template>
                  </v-radio>
                </v-radio-group>
                <v-textarea
                  v-if="showReasonField"
                  ref="status"
                  v-model="pendingReason"
                  dense
                  :rules="
                    issueStatus === 'pending'
                      ? [(v) => !!v || 'This field is required']
                      : []
                  "
                  class="field-text-style"
                  hint="Only required if Issue Status is 'Pending'."
                  persistent-hint
                  color="#000000"
                  light
                  outlined
                  background-color="#FFF"
                  label="Reason*"
                />
                <v-textarea
                  v-if="showRootCauseField"
                  ref="status"
                  v-model="rootCause"
                  dense
                  :rules="
                    issueStatus === 'completed'
                      ? [(v) => !!v || 'This field is required']
                      : []
                  "
                  class="field-text-style"
                  hint="Only required if Issue Status is 'Completed'."
                  persistent-hint
                  color="#000000"
                  light
                  outlined
                  background-color="#FFF"
                  label="Root Cause of Issue*"
                />
              </div>
            </v-col>
          </div>
        </v-col>
        <v-col style="border: 1px solid gray; border-radius: 10px">
          <check-in-clock style="color: #ff3700; font-size: 14px" />
        </v-col>
      </v-form>
      <v-btn
        fab
        color="#FFF"
        small
        rounded
        elevation="0"
        class="close-button"
        @click="showModal = false"
      >
        <v-icon color="#37474f">
          mdi mdi-close
        </v-icon>
      </v-btn>
      <v-btn
        v-if="checkoutLock"
        color="#FF3700"
        class="btn-style"
        elevation="0"
      >
        <span style="color: #fff;">Loading...</span>
      </v-btn>
      <v-btn
        v-else
        color="#FF3700"
        class="btn-style"
        elevation="0"
        @click="checkOut"
      >
        <span style="color: #fff;">Check-Out</span>
      </v-btn>
      <center-spinner :loading="loading" />
    </div>
  </v-dialog>
</template>

<script>
import spinner from '../../components/SpinnerCentre.vue';
import CheckInClock from './CheckInClock.vue';
import moment from 'moment';
import OverlayAlert from 'src/views/components/OverlayAlert';
import Constants from 'src/constants';
import { getTimeZone } from '../../../utils/timezone';
export default {
  name: 'CheckInSheet',
  components: {
    'center-spinner': spinner,
    'check-in-clock': CheckInClock,
    'overlay-alert': OverlayAlert,
  },
  filters: {
    arrangePretty (val) {
      if (val === 'staff') {
        return 'Site Manager';
      } else {
        return val;
      }
    },
  },
  props: {
    previousGate: {
      type: String,
      required: false,
    },
    locationEnableCheck: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    loading: false,
    scannedCode: '',
    showModal: true,
    selectedGate: null,
    selectedGateLocation: null,
    selectedProject: null,
    selectedCheckinType: null,
    isOnsiteSelected: true,
    selectedProjectIssue: '',
    isOffsiteSelected: false,
    showInstancesForm: false,
    selectedProjectName: null,
    locationFetchFailed: false,
    visitPurpose: null,
    selectedContract: null,
    answerSelected: {},
    checkoutType: 'onsite',
    verificationFailed: false,
    verificationFailedMessage: '',
    showProgress: false,
    selectedBudgetItem: null,
    valid: false,
    issueStatus: null,
    pendingReason: null,
    rootCause: null,
    showReasonField: false,
    showRootCauseField: false,
    visitStatus: null,
    updatedContractProgress: 0,
    currentContractProgress: 0,
    updatedProjectProgress: null,
    currentProjectProgress: 0,
    manualCheckoutReason: '',
    checkOutTime: moment().format('YYYY-MM-DD HH:mm:ss'),
    minDate: moment().format('YYYY-MM-DD'),
    maxTime: moment().format('HH:mm:ss'),
    light: true,
    filled: true,
    outlined: true,
    dense: true,
    longitudeValue: null,
    latitudeValue: null,
    processAlert: '',
    checkoutLock: false,
  }),
  computed: {
    selectedOrganization () {
      return this.$store.getters.getSelectedOrganization;
    },
    isStoredOrganizationAvailable () {
      if (Object.keys(this.selectedOrganization).length > 0) {
        return true;
      }
      return false;
    },
    locationCheck () {
      return this.orgSettings.location_verification;
    },
    userTimeZone () {
      const timezone = getTimeZone();
      return timezone;
    },
    minTime () {
      return moment(this.activeCheckInDetails.checkin_time).format(
        'YYYY-MM-DD HH:mm:ss',
      );
    },
    isWorkTypeIssue () {
      if (
        this.activeCheckInDetails.type === 'worker' &&
        this.isActiveCheckInDetailsFetched === true &&
        this.activeCheckInDetails.work_details !== null &&
        this.activeCheckInDetails.work_details.type === 'project-issue'
      ) {
        return true;
      }
      return false;
    },
    gatesList () {
      return this.$store.getters['projects/getOrganizationProjectGates'];
    },
    organizationProjects () {
      return this.$store.getters['projects/getOrganizationProjects'];
    },
    orgSettings () {
      return this.$store.getters['organization/getOrganizationSettings'];
    },
    checkInId () {
      return this.$store.getters['swiftuser/getCheckInId'];
    },
    filteredProjects () {
      if (!this.selectedProject || typeof this.selectedProject !== 'string') {
        return [];
      }
      const query = this.selectedProject.toLowerCase();
      return this.organizationProjects.filter((project) =>
        project.name.toLowerCase().includes(query),
      );
    },
    questions () {
      return this.$store.getters['questions/getOrgQuestions'];
    },
    activeCheckInDetails () {
      return this.$store.getters['swiftuser/getActiveCheckInDetails'];
    },
    isActiveCheckInDetailsFetched () {
      if (Object.keys(this.activeCheckInDetails).length > 0) {
        return true;
      }
      return false;
    },
  },
  watch: {
    showModal: function (newval, oldval) {
      if (newval === false) {
        this.$emit('closed');
      }
    },
    selectedGate (newVal) {
      let gate = null;
      if (newVal !== null) {
        gate = this.gatesList.find(function (g) {
          return g.id === newVal || (newVal && g.id === newVal.id);
        });
      }

      if (gate) {
        if (gate.project && gate.project.name) {
          this.selectedProjectName = gate.project.name;
        } else {
          this.selectedProjectName = '-';
        }
        if (gate.location_name) {
          this.selectedGateLocation = gate.location_name;
        } else if (gate.address && gate.address.line1) {
          this.selectedGateLocation = gate.address.line1;
        } else if (gate.name) {
          this.selectedGateLocation = gate.name;
        } else {
          this.selectedGateLocation = '—';
        }
      } else {
        this.selectedGateLocation = '—';
      }
      if (newVal !== null || newVal !== '') {
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
        // if(this.previousGate === null || this.previousGate === undefined){
        // this.verifyLocation();
        // }
      }
    },
    checkOutTime (newValue) {
      if (newValue === null) {
        this.checkOutTime = moment().format('YYYY-MM-DD HH:mm:ss');
      }
    },
  },
  async mounted () {
    await this.fetchLocationDetails();
    await this.fetchActiveCheckInDetails();
    await this.fetchOrgDetails();
    await this.getOrganizationProjectGates();
    this.loading = true;
    if (this.previousGate !== undefined && this.previousGate !== null) {
      this.selectedGate = this.previousGate;
      // this.verifyLocation();
    }
    if (this.activeCheckInDetails.type === 'contractor') {
      this.currentContractProgress =
        this.activeCheckInDetails.contract_details.contract.contract_progress;
      this.updatedContractProgress =
        this.activeCheckInDetails.contract_details.contract.contract_progress;
    }
    if (this.activeCheckInDetails.type === 'staff') {
      this.currentProjectProgress =
        this.activeCheckInDetails.project.project_progress;
      this.updatedProjectProgress =
        this.activeCheckInDetails.project.project_progress;
    }
    this.loading = false;
  },
  methods: {
    isCheckoutTypeChanged () {
      if (this.checkoutType === 'onsite') {
        this.isOffsiteSelected = false;
        this.isOnsiteSelected = true;
      } else {
        this.verificationFailed = false;
        this.verificationFailedMessage = '';
        this.isOnsiteSelected = false;
        this.isOffsiteSelected = true;
      }
    },
    isVisitStatusChanged () {
      if (this.visitStatus === 'completed') {
        this.$refs.status.reset();
      }
    },
    isIssueStatusChanged () {
      if (this.issueStatus === 'completed') {
        this.pendingReason = null;
        this.showReasonField = false;
        this.showRootCauseField = true;
      } else {
        this.rootCause = null;
        this.showRootCauseField = false;
        this.showReasonField = true;
      }
    },
    async fetchActiveCheckInDetails () {
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/fetchUsersCurrentCheckInDetails')
        .then((response) => {
          // if (this.isActiveCheckInDetailsFetched === true) {
          //   this.loading = false;
          //   return;
          // }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    async fetchOrgDetails () {
      this.loading = true;
      await this.$store.dispatch(
        'organization/fetchOrganizationSettings',
        this.selectedOrganization,
      );
      this.loading = false;
    },
    convertToGmt (date) {
      return moment(date).utc();
    },
    resetPage () {
      this.selectedGate = null;
      this.selectedProject = null;
      this.selectedCheckinType = null;
      this.isOnsiteSelected = false;
      this.selectedProjectIssue = '';
      this.isOffsiteSelected = false;
      this.showInstancesForm = false;
      this.locationFetchFailed = false;
      this.visitPurpose = null;
      this.selectedContract = null;
      this.answerSelected = {};
      this.checkoutType = null;
      this.verificationFailed = false;
      this.verificationFailedMessage = '';
      this.showProgress = false;
      this.selectedBudgetItem = null;
    },
    async getOrganizationProjectGates () {
      this.loading = true;
      const projectId = this.activeCheckInDetails.project.id;
      await this.$store
        .dispatch('projects/fetchOrganizationProjectGates', {
          projectId: projectId,
        })
        .then(() => {
          if (this.gatesList.length === 1) {
            this.selectedGate = this.gatesList[0].id;
            this.selectedGateLocation = this.gatesList[0].location_name;
          }
        });
      this.loading = false;
    },
    fetchLocationDetails () {
      if (!this.locationEnableCheck) {
        return;
      }
      if (navigator.geolocation) {
        this.showProgress = true;
        this.processAlert = 'Checking Out...';
        navigator.geolocation.getCurrentPosition(
          this.showPosition.bind(this),
          this.showError.bind(this),
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
        );
      } else {
        this.showProgress = false;
        this.verificationFailed = true;
        this.verificationFailedMessage =
          'Geolocation is not supported by this browser.';
      }
    },
    showPosition (position) {
      this.latitudeValue = position.coords.latitude;
      this.longitudeValue = position.coords.longitude;
      this.showProgress = false;
    },
    showError (error) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage =
            'User denied the request for location access. Please close the tab and relaunch our app once access is enabled.';
          break;
        case error.POSITION_UNAVAILABLE:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage =
            'Location information is unavailable.';
          break;
        case error.TIMEOUT:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage =
            'The request to get user location timed out.';
          break;
        case error.UNKNOWN_ERROR:
          this.showProgress = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage = 'An unknown error occurred.';
          break;
      }
    },
    async verifyLocation () {
      if (this.locationCheck === false) {
        return;
      }
      this.$store.dispatch('swiftuser/clearScannedLocationName');
      this.loading = true;
      await this.$store
        .dispatch('swiftuser/verifyLocation', {
          orgExtId: this.selectedOrganization.organization.external_id,
          details: {
            project_gate_id: this.selectedGate,
            checkin_latitude:
              this.latitudeValue != null ? this.latitudeValue.toString() : null,
            checkin_longitude:
              this.longitudeValue != null
                ? this.longitudeValue.toString()
                : null,
            location_access: !!(
              this.longitudeValue != null && this.latitudeValue != null
            ),
            checkin_type: 'check-out',
          },
        })
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.locationFetchFailed = true;
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
        });
    },

    async checkOut () {
      this.checkoutType !== 'offsite' &&
        (await this.locationCheck) &&
        (await this.verifyLocation());
      if (this.checkoutType === 'offsite') {
        if (this.checkOutTime === null || this.checkOutTime === '') {
          this.$store.dispatch(
            'alert/onAlert',
            {
              message: 'Please enter the check-out time.',
              type: Constants.ALERT_TYPE_ERROR,
            },
            { root: true },
          );
          return;
        }
        const checkOutTime = moment(this.checkOutTime, 'YYYY-MM-DD HH:mm:ss');
        const minTime = moment(this.minTime, 'YYYY-MM-DD HH:mm:ss');
        const currentTime = moment();

        if (!checkOutTime.isBetween(minTime, currentTime)) {
          this.$store.dispatch(
            'alert/onAlert',
            {
              message: 'Please enter correct check-out time.',
              type: Constants.ALERT_TYPE_ERROR,
            },
            { root: true },
          );
          return;
        }
      }

      if (this.$refs.form.validate() === false) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'Fill all required fields.',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      if (this.verificationFailedMessage !== '' && !this.locationFetchFailed) {
        return;
      }
      if (this.checkoutLock) return;
      if (this.activeCheckInDetails.type === 'contractor') {
        if (this.checkoutType === 'offsite') {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: this.convertToGmt(this.checkOutTime),
                type: 'manual-offsite',
                manual_checkout_reason: this.manualCheckoutReason,
                checkout_gate: null,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              localStorage.removeItem('breakTime');
              this.completeContractorCheckOutProcess();
              this.checkoutLock = false;
            })
            .catch((errors) => {
              this.checkoutLock = false;
            });
        } else {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: moment.utc().format(),
                type: 'manual-onsite',
                manual_checkout_reason: null,
                checkout_gate: this.selectedGate,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              localStorage.removeItem('breakTime');
              this.completeContractorCheckOutProcess();
              this.checkoutLock = false;
            })
            .catch((errors) => {
              this.checkoutLock = false;
            });
        }
      }
      if (this.activeCheckInDetails.type === 'worker') {
        if (this.checkoutType === 'offsite') {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: this.convertToGmt(this.checkOutTime),
                type: 'manual-offsite',
                manual_checkout_reason: this.manualCheckoutReason,
                checkout_gate: null,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              if (this.isWorkTypeIssue) {
                localStorage.removeItem('breakTime');
                this.completeWorkerCheckOutProcess();
                this.checkoutLock = false;
                return;
              }
              this.$store.dispatch('alert/onAlert', {
                message: 'Check-out completed successfully.',
                type: Constants.ALERT_TYPE_SUCCESS,
              });
              localStorage.removeItem('breakTime');
              localStorage.removeItem('checkInTime');
              localStorage.removeItem('checkIn');
              this.$emit('reload');
              this.checkoutLock = false;
              // this.$router.push({ name: "HomePage" });
            })
            .catch((errors) => {
              this.checkoutLock = false;
            });
        } else {
          if (this.isWorkTypeIssue) {
            this.checkoutLock = true;
            this.$store
              .dispatch('swiftuser/checkOutUser', {
                checkOutTime: {
                  checkout_time: moment.utc().format(),
                  type: 'manual-onsite',
                  manual_checkout_reason: null,
                  checkout_gate: this.selectedGate,
                  latitude: null,
                  longitude: null,
                },
                checkInId: this.activeCheckInDetails.id,
              })
              .then((response) => {
                localStorage.removeItem('breakTime');
                this.completeWorkerCheckOutProcess();
                this.checkoutLock = false;
              })
              .catch((error) => {
                this.verificationFailed = true;
                this.verificationFailedMessage = error.response.data.message;
                this.checkoutLock = false;
              });
            return;
          }
          this.checkoutLock = true;
          await this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: moment.utc().format(),
                type: 'manual-onsite',
                manual_checkout_reason: null,
                checkout_gate: this.selectedGate,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              this.$store.dispatch('alert/onAlert', {
                message: 'Check-out completed successfully.',
                type: Constants.ALERT_TYPE_SUCCESS,
              });
              localStorage.removeItem('breakTime');
              localStorage.removeItem('checkInTime');
              localStorage.removeItem('checkIn');
              this.$emit('reload');
              this.checkoutLock = false;
              // this.$router.push({ name: "HomePage" });
            })
            .catch((error) => {
              this.verificationFailed = true;
              this.verificationFailedMessage = error.response.data.message;
              this.checkoutLock = false;
            });
        }
      }
      if (this.activeCheckInDetails.type === 'visitor') {
        if (this.isOffsiteSelected) {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: this.convertToGmt(this.checkOutTime),
                type: 'manual-offsite',
                manual_checkout_reason: this.manualCheckoutReason,
                checkout_gate: null,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              localStorage.removeItem('breakTime');
              this.completeVisitorCheckOutProcess();
              this.checkoutLock = false;
            })
            .catch((errors) => {
              this.checkoutLock = false;
            });
          return;
        }
        if (this.isOnsiteSelected) {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: moment.utc().format(),
                type: 'manual-onsite',
                manual_checkout_reason: null,
                checkout_gate: this.selectedGate,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              localStorage.removeItem('breakTime');
              this.checkoutLock = false;
              this.completeVisitorCheckOutProcess();
            })
            .catch((error) => {
              this.verificationFailed = true;
              this.verificationFailedMessage = error.response.data.message;
              this.checkoutLock = false;
            });
          return;
        }
      }
      if (this.activeCheckInDetails.type === 'staff') {
        if (this.checkoutType === 'offsite') {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: this.convertToGmt(this.checkOutTime),
                type: 'manual-offsite',
                manual_checkout_reason: this.manualCheckoutReason,
                checkout_gate: null,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              localStorage.removeItem('breakTime');
              this.checkoutLock = false;
              this.completeStaffCheckOutProcess();
            })
            .catch((error) => {
              this.verificationFailed = true;
              this.verificationFailedMessage = error.response.data.message;
              this.checkoutLock = false;
            });
        } else {
          this.checkoutLock = true;
          this.$store
            .dispatch('swiftuser/checkOutUser', {
              checkOutTime: {
                checkout_time: moment.utc().format(),
                type: 'manual-onsite',
                manual_checkout_reason: null,
                checkout_gate: this.selectedGate,
                latitude: null,
                longitude: null,
              },
              checkInId: this.activeCheckInDetails.id,
            })
            .then((response) => {
              localStorage.removeItem('breakTime');
              this.checkoutLock = false;
              this.completeStaffCheckOutProcess();
            })
            .catch((errors) => {
              this.checkoutLock = false;
            });
        }
      }
    },
    async completeStaffCheckOutProcess () {
      this.checkoutLock = true;
      await this.$store
        .dispatch('swiftuser/sendProjectProgress', {
          projectProgress: {
            project_progress:
              this.updatedProjectProgress !== null
                ? this.updatedProjectProgress.toString()
                : '0',
          },
          checkInId: this.activeCheckInDetails.id,
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-out completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          localStorage.removeItem('breakTime');
          localStorage.removeItem('checkInTime');
          localStorage.removeItem('checkIn');
          this.$emit('reload');
          this.checkoutLock = false;
          // this.$router.push({ name: "HomePage" });
        })
        .catch(() => {
          this.checkoutLock = false;
        });
    },
    async completeContractorCheckOutProcess () {
      this.checkoutLock = true;
      await this.$store
        .dispatch('swiftuser/sendContractProgress', {
          contractProgress: {
            contract_progress:
              this.updatedContractProgress !== null
                ? this.updatedContractProgress.toString()
                : '0',
          },
          checkInId: this.activeCheckInDetails.id,
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-out completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          localStorage.removeItem('breakTime');
          localStorage.removeItem('checkInTime');
          localStorage.removeItem('checkIn');
          this.$emit('reload');
          this.checkoutLock = false;
          // this.$router.push({ name: "HomePage" });
        })
        .catch(() => {
          this.checkoutLock = false;
        });
    },
    async completeVisitorCheckOutProcess () {
      this.checkoutLock = true;
      await this.$store
        .dispatch('swiftuser/sendVisitStatus', {
          statusDetails: {
            status: this.visitStatus,
            pending_reason: this.pendingReason,
          },
          checkInId: this.activeCheckInDetails.id,
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-out completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          localStorage.removeItem('breakTime');
          localStorage.removeItem('checkInTime');
          localStorage.removeItem('checkIn');
          this.$emit('reload');
          this.checkoutLock = false;
          // this.$router.push({ name: "HomePage" });
        })
        .catch(() => {
          this.checkoutLock = false;
        });
    },
    async completeWorkerCheckOutProcess () {
      this.checkoutLock = true;
      await this.$store
        .dispatch('swiftuser/sendIssueStatus', {
          statusDetails: {
            status: this.issueStatus,
            pending_reason: this.pendingReason,
            root_cause: this.rootCause,
          },
          checkInId: this.activeCheckInDetails.id,
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'Check-out completed successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          localStorage.removeItem('breakTime');
          localStorage.removeItem('checkInTime');
          localStorage.removeItem('checkIn');
          this.$emit('reload');
          this.checkoutLock = false;
          // this.$router.push({ name: "HomePage" });
        })
        .catch(() => {
          this.checkoutLock = false;
        });
    },
  },
};
</script>
<style scoped>
.theme--dark.v-list {
  background-color: white !important;
}
.v-input--selection-controls__input .v-icon {
  color: #ccc !important;
}
.theme--dark >>> .v-list-item {
  color: #37474f !important;
}
.theme--dark.v-label {
  color: rgba(255, 255, 255, 0.7);
}
.theme--dark.v-messages {
  color: #37474f !important;
}
.hint-text {
  font-size: 13px;
  color: #444444;
  margin-top: -13px !important;
}
.class-disabled .v-input--selection-controls__input .v-icon {
  color: #000 !important;
}
.disabled-class {
  color: #ccc !important;
}
.checkin .mdi-checkbox-marked::before {
  color: #0000 !important;
  caret-color: #0000 !important;
}
.small-select {
  text-transform: capitalize !important;
}
::v-deep .v-input--selection-controls__input .v-icon {
  color: rgb(8, 8, 8) !important;
}
.v-list-item__title {
  color: #0000 !important;
}
.v-radio {
  display: flex;
  justify-content: center;
  align-items: center;
}
.v-input--selection-controls {
  margin: 0px !important;
  padding-top: 0px !important;
}
.type-bottom {
  padding: 0px !important;
  margin: 0px !important;
}
.content-section {
  height: 100vh;
}
.square-chip {
  width: 200px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  white-space: normal;
  font-size: 14px;
}
.checkout-type {
  padding: 0px;
  margin: 0px;
}
.header-style {
  text-transform: capitalize;
}
.location-wrapper {
  margin-top: 8px;
  margin-bottom: 8px;
}

.location-label-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.location-icon {
  color: #9e9e9e; /* dull icon */
}

.location-label {
  font-size: 12px;
  color: #9e9e9e; /* dull label */
  font-weight: 500;
}

.location-value {
  margin-left: 20px; /* aligns under text, not icon */
  font-size: 15px;
  font-weight: 600;
  color: #000000; /* strong black */
}
</style>
